import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSavedHeaderViews } from "components/CustomComponents/useSavedHeaderViews";
import { SavedViewSelector } from "components/CustomComponents/SavedViewSelector";

// Shared saved-view wiring for the Roles and User Groups list screens, so
// both keep the app-wide list toolbar (Default View / saved views, search,
// download, sort, filter via CustomFilter) the same way every other list
// screen does. This is the logic both screens used to carry inline.
//
// Returns:
//   titleOverride  -> pass to CustomFilter's titleOverride (the view picker)
//   visibleHeaders -> the active view's columns, in order; pass to
//                     CustomFilter's filterHeaders and render the table from it
//   subModuleId    -> for FilterProvider
//
// legacyHeaders ({ key: oldDefaultName }) lists the columns the screen had
// before its redesign. A saved view that only knows those keys was saved
// before the new columns existed, so it is upgraded on load: the new
// columns are added in their standard position, and any column still
// carrying its old default label gets the current label. Views that
// already mention a new column were saved after the redesign and are
// used exactly as saved.
export const upgradeLegacyView = (view, headers, legacyHeaders) => {
  if (!view || !legacyHeaders) return view;
  const saved = view.headerConfig?.length
    ? view.headerConfig
    : (view.headerKeys ?? []).map((key) => ({ key }));
  if (!saved.length) return view;
  const isLegacy = saved.every((entry) => entry.key in legacyHeaders);
  if (!isLegacy) return view;
  const savedByKey = new Map(saved.map((entry) => [entry.key, entry]));
  const headerConfig = headers
    .filter((header) => savedByKey.has(header.key) || !(header.key in legacyHeaders))
    .map((header) => {
      const savedName = savedByKey.get(header.key)?.name?.trim();
      const keepSavedName = savedName && savedName !== legacyHeaders[header.key];
      return { key: header.key, name: keepSavedName ? savedName : header.name };
    });
  return { ...view, headerConfig, headerKeys: headerConfig.map((entry) => entry.key) };
};

export const useListToolbar = ({
  headers,
  defaultViewId,
  storageKey,
  domain,
  userDetails,
  userScreenAccess,
  subModuleName,
  screenAccess,
  onViewChange,
  legacyHeaders,
}) => {
  const subModuleId = userScreenAccess?.module_details
    ?.find((mod) => mod.module_name === "Roles & User Group")
    ?.submodules?.find((sub) => sub.sub_module_name === subModuleName)?.sub_module_id;

  const handleViewChange = useCallback(() => onViewChange?.(), [onViewChange]);

  const {
    savedViewsForScope: rawSavedViews,
    activeViewId,
    selectView,
    selectDefaultView,
    deleteView,
    createView,
    upsertView,
    updateView,
  } = useSavedHeaderViews({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey,
    onViewChange: handleViewChange,
    scope: "default",
    defaultViewId,
  });

  const savedViewsForScope = useMemo(
    () => rawSavedViews.map((view) => upgradeLegacyView(view, headers, legacyHeaders)),
    [headers, legacyHeaders, rawSavedViews],
  );

  const persistedDefaultViewId = activeViewId || defaultViewId;
  const [currentViewId, setCurrentViewId] = useState(null);

  useEffect(() => {
    setCurrentViewId((prev) => (prev && prev !== defaultViewId ? prev : persistedDefaultViewId));
  }, [defaultViewId, persistedDefaultViewId]);

  const storedDefaultView = useMemo(
    () => savedViewsForScope.find((view) => view.id === defaultViewId) || null,
    [defaultViewId, savedViewsForScope],
  );
  const customSavedViews = useMemo(
    () => savedViewsForScope.filter((view) => view.id !== defaultViewId),
    [defaultViewId, savedViewsForScope],
  );

  const defaultView = useMemo(
    () =>
      (storedDefaultView?.isCustomDefault ? storedDefaultView : null) || {
        id: defaultViewId,
        name: "Default View",
        headerConfig: headers.map((header) => ({ key: header.key, name: header.name })),
        headerKeys: headers.map((header) => header.key),
        isDefaultView: true,
      },
    [defaultViewId, headers, storedDefaultView],
  );

  const effectiveViewId = currentViewId ?? persistedDefaultViewId;
  const activeView = useMemo(
    () =>
      effectiveViewId === defaultViewId
        ? defaultView
        : customSavedViews.find((view) => view.id === effectiveViewId) || defaultView,
    [customSavedViews, defaultView, defaultViewId, effectiveViewId],
  );

  const visibleHeaders = useMemo(() => {
    const byKey = new Map(headers.map((header) => [header.key, header]));
    if (activeView?.headerConfig?.length) {
      const ordered = activeView.headerConfig
        .map((entry) => {
          const base = byKey.get(entry.key);
          return base ? { ...base, name: entry.name?.trim() || base.name } : null;
        })
        .filter(Boolean);
      return ordered.length ? ordered : headers;
    }
    if (activeView?.headerKeys?.length) {
      const keys = new Set(activeView.headerKeys);
      const filtered = headers.filter((header) => keys.has(header.key));
      return filtered.length ? filtered : headers;
    }
    return headers;
  }, [activeView, headers]);

  const titleOverride = (
    <SavedViewSelector
      headers={headers}
      defaultHeaders={headers}
      defaultView={defaultView}
      savedViews={customSavedViews}
      activeView={activeView}
      activeViewId={effectiveViewId}
      persistedDefaultViewId={persistedDefaultViewId}
      screenAccess={screenAccess}
      onSelectDefaultView={() => {
        setCurrentViewId(defaultViewId);
        handleViewChange();
      }}
      onSelectView={(viewId) => {
        setCurrentViewId(viewId);
        handleViewChange();
      }}
      onCreateView={(viewName, headerConfig) => createView(viewName, headerConfig, { activate: false })}
      onUpdateView={(viewId, viewName, headerConfig) => {
        if (viewId === defaultViewId) {
          upsertView(
            defaultViewId,
            viewName || "Default View",
            headerConfig,
            "Default view updated successfully.",
            { isCustomDefault: true },
          );
          setCurrentViewId(defaultViewId);
          return;
        }
        updateView(viewId, viewName, headerConfig);
      }}
      onDeleteView={(viewId) => {
        if (currentViewId === viewId) setCurrentViewId(defaultViewId);
        deleteView(viewId);
      }}
      onMakeDefaultView={(viewId) => {
        if (viewId === defaultViewId) selectDefaultView();
        else selectView(viewId);
        setCurrentViewId(viewId);
      }}
    />
  );

  return { titleOverride, visibleHeaders, subModuleId };
};

// CustomFilter's rendered height isn't fixed (its chip row appears once a
// filter/sort is applied), so the sticky table header has to measure it.
export const useMeasuredHeight = () => {
  const [node, setNode] = useState(null);
  const [height, setHeight] = useState(0);
  const ref = useCallback((element) => setNode(element), []);
  useEffect(() => {
    if (!node) return undefined;
    const update = () => setHeight(node.offsetHeight);
    update();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);
  return [ref, height];
};
